/**
 * FooterLinks.tsx — Footer Navigation Block
 * 
 * 3-tier priority system for maximum compatibility:
 * 
 * 1. MAGENTO CMS BLOCK — If the store has existing footer_links, footer_links_company,
 *    etc. CMS static blocks, render them as-is. This means migrating an existing
 *    Magento site "just works" — existing footer content appears automatically.
 * 
 * 2. ADMIN-CONFIGURED LINKS — If the user configures link groups in the visual
 *    editor (data.linkGroups), use those. Full control from the admin UI.
 * 
 * 3. SMART DEFAULTS — If neither CMS block nor admin links exist, show sensible
 *    defaults that link to standard Magento pages (shop, help, company, legal).
 * 
 * Config options:
 *   - source: 'auto' | 'cms' | 'manual' — which tier to use
 *   - cmsBlockIdentifier: string — CMS block ID (default: 'footer_links')
 *   - linkGroups: { title, links[] }[] — manually configured link groups
 *   - columns: number — number of columns (2-6)
 *   - showTitle: boolean — show group titles
 *   - variant: string — visual style ('default', 'minimal', 'compact')
 */
import React, { useEffect, useState } from 'react';

interface LinkItem {
    label: string;
    href: string;
}

interface LinkGroup {
    title: string;
    links: LinkItem[];
}

interface FooterLinksProps {
    data: {
        source?: 'auto' | 'cms' | 'manual';
        cmsBlockIdentifier?: string;
        linkGroups?: LinkGroup[];
        columns?: number;
        showTitle?: boolean;
        variant?: string;
        [key: string]: unknown;
    };
    styles?: string;
    variant?: string;
    context?: unknown;
}

// Default link groups — used when no CMS block and no admin config
const DEFAULT_LINK_GROUPS: LinkGroup[] = [
    {
        title: 'Shop',
        links: [
            { label: 'New Arrivals', href: '/new-arrivals' },
            { label: 'Best Sellers', href: '/best-sellers' },
            { label: 'Sale', href: '/sale' },
            { label: 'All Products', href: '/category' },
        ],
    },
    {
        title: 'Help',
        links: [
            { label: 'FAQ', href: '/faq' },
            { label: 'Shipping', href: '/shipping' },
            { label: 'Returns', href: '/returns' },
            { label: 'Contact', href: '/contact' },
        ],
    },
    {
        title: 'Company',
        links: [
            { label: 'About', href: '/about' },
            { label: 'Careers', href: '/careers' },
            { label: 'Blog', href: '/blog' },
            { label: 'Press', href: '/press' },
        ],
    },
    {
        title: 'Legal',
        links: [
            { label: 'Privacy Policy', href: '/privacy' },
            { label: 'Terms & Conditions', href: '/terms' },
            { label: 'Cookie Policy', href: '/cookies' },
        ],
    },
];

export const FooterLinks: React.FC<FooterLinksProps> = ({ data, styles, variant, context }) => {
    const {
        source = 'auto',
        cmsBlockIdentifier = 'footer_links',
        linkGroups,
        columns = 4,
        showTitle = true,
    } = data ?? {};

    const effectiveVariant = variant || data?.variant;
    const isBentoDark = effectiveVariant === 'bento-dark';

    // CMS block content comes from the synced data (SSR/context) only.
    const [cmsLoading, setCmsLoading] = useState(source === 'auto' || source === 'cms');
    const [, setCmsChecked] = useState(false);

    // Check if CMS block content was passed from SSR context or _cmsCache
    interface CmsCacheEntry { content?: string }
    interface FooterCtx { footerCmsBlock?: { content?: string }; cmsBlocks?: Record<string, CmsCacheEntry> }
    const ctx = context as FooterCtx | undefined;
    const cmsCacheEntry: CmsCacheEntry | undefined = (data?._cmsCache as Record<string, CmsCacheEntry> | undefined)?.[cmsBlockIdentifier];
    const ssrCmsContent: string | null = cmsCacheEntry?.content
        ?? ctx?.footerCmsBlock?.content
        ?? ctx?.cmsBlocks?.[cmsBlockIdentifier]?.content
        ?? null;

    useEffect(() => {
        // No live Magento fallback — synced footer content is read from context
        // above (ssrCmsContent). If it isn't in the sync, fall through to the
        // admin-configured links / defaults rather than querying Magento for a
        // possibly-missing block (the "CMS block X doesn't exist" error source).
        setCmsLoading(false);
        setCmsChecked(true);
    }, [source, cmsBlockIdentifier, ssrCmsContent]);

    // Determine what to render based on priority:
    // 1. CMS block content (from SSR or client fetch)
    const effectiveCmsContent: string | null = ssrCmsContent;

    // 2. Admin-configured links
    const effectiveLinks = (linkGroups && linkGroups.length > 0) ? linkGroups : null;

    // Loading state
    if (cmsLoading) {
        return (
            <div className={`${styles ?? ''}`}>
                <div className={`grid gap-8`} style={{ gridTemplateColumns: `repeat(${Math.min(columns, 4)}, 1fr)` }}>
                    {Array.from({ length: Math.min(columns, 4) }).map((_, i) => (
                        <div key={i}>
                            <div className={`h-4 w-20 rounded mb-4 animate-pulse ${isBentoDark ? 'bg-[color-mix(in_srgb,var(--surface-1)_5%,transparent)]' : 'bg-[var(--surface-2,#1e293b)]'}`} />
                            {[1, 2, 3, 4].map(j => (
                                <div key={j} className={`h-3 w-24 rounded mb-3 animate-pulse ${isBentoDark ? 'bg-[color-mix(in_srgb,var(--surface-1)_3%,transparent)]' : 'bg-[var(--surface-2,#1e293b)]'}`} />
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    // TIER 1: CMS Block — render raw HTML from Magento CMS
    if ((source === 'auto' || source === 'cms') && effectiveCmsContent) {
        return (
            <div
                className={`footer-links-cms ${styles ?? ''}`}
                dangerouslySetInnerHTML={{ __html: effectiveCmsContent }}
                style={{
                    display: 'grid',
                    gap: '2rem',
                    gridTemplateColumns: `repeat(auto-fit, minmax(180px, 1fr))`,
                }}
            />
        );
    }

    // TIER 2 or TIER 3: Admin links or defaults
    const groups = effectiveLinks ?? DEFAULT_LINK_GROUPS;

    // Variant: columned (default) | horizontal-bar | accordion-mobile
    // Read from content.variant first, then settings.variant, then data.variant (legacy).
    const rawVariant = (data?.variant)
        ?? ((data as { content?: { variant?: string } } | undefined)?.content?.variant)
        ?? 'columned';
    const layoutVariant: 'columned' | 'horizontal-bar' | 'accordion-mobile' =
        ['columned','horizontal-bar','accordion-mobile'].includes(rawVariant)
            ? rawVariant as 'columned' | 'horizontal-bar' | 'accordion-mobile'
            : 'columned';

    // Variant: horizontal-bar — all links inline in a single row, no group titles.
    if (layoutVariant === 'horizontal-bar') {
        const flatLinks = groups.flatMap(g => g.links);
        return (
            <div className={`${styles ?? ''}`}>
                <ul className="flex flex-wrap justify-center gap-x-6 gap-y-2" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                    {flatLinks.map((link, i) => (
                        <li key={link.href + i}>
                            <a
                                href={link.href}
                                className="text-sm transition-colors footer-link-hover"
                                style={{
                                    color: 'var(--color-footer-link, var(--text-secondary))',
                                    textDecoration: 'none',
                                }}
                                onMouseEnter={(e) => {
                                    const el = e.currentTarget as HTMLElement;
                                    el.style.color = 'var(--color-footer-link-hover, var(--hover-accent, var(--accent-primary)))';
                                    el.style.textDecoration = 'underline';
                                    el.style.textUnderlineOffset = '3px';
                                }}
                                onMouseLeave={(e) => {
                                    const el = e.currentTarget as HTMLElement;
                                    el.style.color = 'var(--color-footer-link, var(--text-secondary))';
                                    el.style.textDecoration = 'none';
                                }}
                            >
                                {link.label}
                            </a>
                        </li>
                    ))}
                </ul>
            </div>
        );
    }

    // Variant: accordion-mobile — columned on desktop, <details> accordions on
    // mobile so each group title is tappable to expand its links list.
    if (layoutVariant === 'accordion-mobile') {
        return (
            <div className={`${styles ?? ''}`}>
                <style dangerouslySetInnerHTML={{ __html: `
                    .footer-links-am { display: grid; gap: 2rem; grid-template-columns: repeat(${Math.min(columns, groups.length)}, 1fr); }
                    .footer-links-am details { background: transparent; }
                    .footer-links-am summary { list-style: none; cursor: pointer; }
                    .footer-links-am summary::-webkit-details-marker { display: none; }
                    @media (max-width: 768px) {
                        .footer-links-am { grid-template-columns: 1fr; gap: 0.5rem; }
                        .footer-links-am details {
                            border-top: 1px solid var(--color-footer-border, var(--glass-border));
                            padding: 0.75rem 0;
                        }
                        .footer-links-am details > summary {
                            display: flex; align-items: center; justify-content: space-between;
                            font-size: 0.875rem; font-weight: 600; padding: 0.25rem 0;
                        }
                        .footer-links-am details > summary::after {
                            content: '+'; font-size: 1.25rem; font-weight: 400;
                            transition: transform 200ms ease;
                        }
                        .footer-links-am details[open] > summary::after { content: '−'; }
                        .footer-links-am details > ul { padding-top: 0.5rem; }
                    }
                ` }} />
                <div className="footer-links-am">
                    {groups.map((group, i) => (
                        <details key={group.title || i} open>
                            {showTitle && (
                                <summary>
                                    <span
                                        className={isBentoDark
                                            ? 'text-[12px] font-bold uppercase tracking-[0.2em] text-[var(--color-footer-heading,var(--text-primary))]'
                                            : 'text-sm font-semibold text-[var(--color-footer-heading, var(--text-primary, #f1f5f9))]'
                                        }
                                    >
                                        {group.title}
                                    </span>
                                </summary>
                            )}
                            <ul style={{ listStyle: 'none', margin: 0, padding: 0, marginTop: '1rem' }}>
                                {group.links.map((link) => (
                                    <li key={link.href} style={{ marginBottom: '0.5rem' }}>
                                        <a
                                            href={link.href}
                                            className="text-sm transition-colors"
                                            style={{
                                                color: 'var(--color-footer-link, var(--text-secondary))',
                                                textDecoration: 'none',
                                            }}
                                        >
                                            {link.label}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </details>
                    ))}
                </div>
            </div>
        );
    }

    // Variant: columned (default) — original grid layout.
    return (
        <div className={`${styles ?? ''}`}>
            <div
                className="footer-links-grid"
                style={{
                    display: 'grid',
                    gap: '2rem',
                    gridTemplateColumns: `repeat(${Math.min(columns, groups.length)}, 1fr)`,
                }}
            >
                {groups.map((group, i) => (
                    <div key={group.title || i}>
                        {showTitle && (
                            <h3
                                className={`mb-4 ${isBentoDark
                                    ? 'text-[12px] font-bold uppercase tracking-[0.2em] text-[var(--color-footer-heading,var(--text-primary))]'
                                    : 'text-sm font-semibold text-[var(--color-footer-heading,var(--text-primary,#f1f5f9))]'
                                    }`}
                            >
                                {group.title}
                            </h3>
                        )}
                        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                            {group.links.map((link) => (
                                <li key={link.href} style={{ marginBottom: '0.5rem' }}>
                                    <a
                                        href={link.href}
                                        className={`transition-colors ${isBentoDark
                                            ? 'text-[13px] text-[var(--color-footer-link,var(--text-secondary))] hover:text-[var(--color-footer-link-hover,var(--accent-primary))]'
                                            : 'text-sm text-[var(--color-footer-link,var(--text-secondary,#94a3b8))] hover:text-[var(--color-footer-link-hover,var(--text-primary,#f1f5f9))]'
                                            }`}
                                        style={{ textDecoration: 'none' }}
                                    >
                                        {link.label}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default FooterLinks;
